import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { Autocomplete } from '@mui/material';
import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import User, { UserRoleFlag, UserRole } from '../../../Model/User';
import UserClaims from '../../../Model/UserClaims';
import DialogRoundedModal from '../../../components/DialogRoundedModal';
import { StyledTextField } from '../../../components/Inputs';
import { SaveButton, CancelButton } from '../../../components/Button/ActionButtons';

import {
  ButtonContainer,
  InputContainer,
  StyledTextLabel,
  AutocompleteTextField,
} from './styles';
import texts from './texts.json';

const AddUserModal = ({
  isOpen,
  setShowUserAddingModal,
  setUserCollection,
  role,
  setIsLoading,
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [liaisonUsers, setLiaisonUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState({ id: null, name: null });

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const fetchInternalUsers = async () => {
      if (role === UserRole.AC) {
        setIsLoading(true);
        const users = await User.getInternalUsers();
        if (isComponentMountedRef.current) {
          const liaisonUsersData = users.filter((user) => user.isLiaison);
          setLiaisonUsers(liaisonUsersData);
          setIsLoading(false);
        }
      } else {
        setLiaisonUsers([]);
      }
    };
    fetchInternalUsers();
  }, [isComponentMountedRef, role, setIsLoading]);

  const onCloseModal = () => {
    setEmail('');
    setEmailError('');
    setSelectedUser({ id: null, name: null });
    setShowUserAddingModal(false);
  };

  const onAddNewUser = async () => {
    setIsLoading(true);
    const userCol = await User.getUserByEmail(email);
    // Check if the user is not found.
    if (!userCol.hasDocs) {
      setEmailError(texts.userNotFound);
      setIsLoading(false);
      return;
    }
    // Check if multiple users are found.
    if (userCol.docs.length > 1) {
      setEmailError((texts.multipleUsersFound));
      setIsLoading(false);
      return;
    }
    const userDoc = userCol.docs[0];
    /*
     * The stored flag, not the `isCAM`-style getter: the getter also answers to `roles`, and this
     * guards a flag-keyed roster. A user holding the role only in `roles` is absent from that list,
     * and refusing them here would leave no way to add them — this modal's write is what sets the
     * flag and puts them back on it.
     */
    if (userDoc.data[UserRoleFlag[role]]) {
      setEmailError(format(texts.userAlreadyExists, { role }));
      setIsLoading(false);
      return;
    }
    try {
      const updateFields = {
        [UserRoleFlag[role]]: true,
        // Internal staff are not activated via Stripe; set so dashboard filters and status logic work.
        'flags.isActive': true,
      };
      if (role === UserRole.AC && selectedUser.id && selectedUser.name) {
        updateFields.leadId = selectedUser.id;
        updateFields.leadName = selectedUser.name;
      }

      // Enable Instant Call for IS users
      if (role === UserRole.IS) {
        updateFields.instaCallIS = true;
      }

      await userDoc.updateFields(updateFields);

      const userClaims = await UserClaims.getUserClaims(userDoc.id);
      if (!userClaims.exists || !userClaims.isAdmin) {
        await userClaims.updateFields({ admin: true });
      }
      const users = await User.getUsersByRole(role);
      if (isComponentMountedRef.current) {
        setUserCollection(users);
      }
      showToast(format(texts.successAddingUser, { role }));
    } catch (error) {
      showToast(format(texts.errorAddingUser, { role }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        onCloseModal();
      }
    }
  };

  return (
    <DialogRoundedModal
      open={isOpen}
      onClose={onCloseModal}
      title={texts.addNewUser}
      fullWidth
      maxWidth="sm"
      actionButtons={(
        <ButtonContainer>
          <SaveButton
            variant="contained"
            onClick={onAddNewUser}
            disabled={!email}
          >
            {texts.addUser}
          </SaveButton>
          <CancelButton onClick={onCloseModal}>{texts.cancel}</CancelButton>
        </ButtonContainer>
        )}
    >
      <InputContainer>
        <StyledTextLabel>{texts.emailInputLabel}</StyledTextLabel>
        <StyledTextField
          autoFocus
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={texts.emailPlaceholder}
          error={!!emailError}
          helperText={!!emailError && emailError}
        />
      </InputContainer>
      {role === UserRole.AC && (
      <InputContainer>
        <StyledTextLabel>{texts.leadInputLabel}</StyledTextLabel>
        <Autocomplete
          fullWidth
          options={liaisonUsers}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={(_, selection) => setSelectedUser({ id: selection?.id, name: selection?.name })}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          renderInput={(params) => (
            <AutocompleteTextField
              {...params}
              fullWidth
              placeholder={texts.selectUserToAssign}
            />
          )}
        />
      </InputContainer>
      )}

    </DialogRoundedModal>
  );
};

AddUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setShowUserAddingModal: PropTypes.func.isRequired,
  setUserCollection: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  setIsLoading: PropTypes.func.isRequired,
};

export default AddUserModal;
